import { createPaginatedEndpoint } from "utils/services.util";

import { PaginatedResponse } from "./common";
import { emptySplitApi } from "./empty-split-api";
import type {
  EmailBrandingPreviewRequest,
  EmailBrandingPreviewResponse
} from "./types/email-branding.type";
import {
  EmailFilters,
  EmailResponse,
  PatchEmailAttach,
  SendEmailInput
} from "./types/email.type";
import type {
  MassEmailResponse,
  SendEmailResponse,
  UploadFileRequest,
  UploadFileResponse
} from "./types/emails.type";

const emailsApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    sendEmail: builder.mutation<
      SendEmailResponse | MassEmailResponse,
      SendEmailInput
    >({
      query: (data) => {
        return {
          url: "/emails/",
          method: "POST",
          body: data
        };
      },
      // A send changes server state two caches read: the Emails list, and — when the payload
      // carried `ambient_scribe_note` — that note's `sent_email_id`. Neither was invalidated
      // (nothing anywhere invalidated "Emails"), so the "Emailed" indicator would not appear until
      // something unrelated happened to refetch. Same fix `useSendFaxMutation` needed for
      // PhysicianCommunications.
      invalidatesTags: ["Emails", "AmbientScribeNotes"]
    }),
    attachFilesToEmails: builder.mutation<SendEmailResponse, PatchEmailAttach>({
      query: ({ ids, should_send, files }) => {
        const formData = new FormData();
        formData.append("should_send", String(should_send));
        ids.forEach((id) => {
          formData.append(`ids`, String(id));
        });
        files.forEach((file) => {
          formData.append(`files`, file);
        });

        return {
          url: "/emails/attach/",
          method: "PATCH",
          body: formData
        };
      }
    }),
    /**
     * One sent email, for previewing what actually went out. `Email.html` is written back at send
     * time, so this is the delivered body rather than a re-render of the draft.
     *
     * `keepUnusedDataFor: 0` for the same reason as `getPhysicianCommunication`: attachment URLs
     * are pre-signed and expire, so this must never be served from cache.
     */
    getEmail: builder.query<EmailResponse, { emailId: number }>({
      query: ({ emailId }) => ({ url: `/emails/${emailId}/` }),
      keepUnusedDataFor: 0
    }),
    getEmailsPaginated: createPaginatedEndpoint<EmailFilters, EmailResponse[]>(
      builder,
      {
        queryFn: (params) => ({
          url: "/emails/",
          params
        }),
        providesTags: ["Emails"],
        transformResponseFn: (response: PaginatedResponse<EmailResponse>) =>
          response.results
      }
    ),
    /**
     * The email as it would be sent, rendered by the same backend path as a real send. Writes
     * nothing and sends nothing; a mutation only because it is a POST.
     */
    getEmailBrandingPreview: builder.mutation<
      EmailBrandingPreviewResponse,
      EmailBrandingPreviewRequest
    >({
      query: (body) => ({
        url: "/emails/branding-preview/",
        method: "POST",
        body
      })
    }),
    uploadFile: builder.mutation<UploadFileResponse, UploadFileRequest>({
      query: ({ content, extension }) => {
        if (typeof content === "string") {
          return {
            url: "/emails/upload-file/",
            method: "POST",
            body: {
              content,
              extension
            }
          };
        } else {
          const formData = new FormData();
          formData.append("content", new File([content], "file." + extension));
          formData.append("extension", extension);
          return {
            url: "/emails/upload-file/",
            method: "POST",
            body: formData
          };
        }
      }
    })
  })
});

export const {
  useSendEmailMutation,
  useAttachFilesToEmailsMutation,
  useGetEmailQuery,
  useGetEmailsPaginatedQuery,
  useGetEmailBrandingPreviewMutation,
  useUploadFileMutation
} = emailsApi;
